import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Link as LinkIcon, X, ShieldCheck, Loader2,
         TrendingDown, TrendingUp, AlertTriangle, Building2, Users, Search,
         Pencil, Check, Wallet, CheckCircle2 } from "lucide-react";

/**
 * Bank Statement Reconciliation.
 * Upload a bank CSV/PDF → parse → auto-match debits→purchases, credits→invoices.
 * Manual Match modal supports:
 *   • search across invoices/purchases (name, number, GSTIN, amount)
 *   • override narration on the txn
 *   • partial payment amount (defaults to txn amount)
 * Fully-matched targets show a "Mark as Paid" button — admin confirms.
 */
export default function BankReconPage() {
    const [statements, setStatements] = useState([]);
    const [selectedStmt, setSelectedStmt] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [uploading, setUploading] = useState(false);
    const [manualForTxn, setManualForTxn] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const loadStatements = () => api.get("/bank/statements").then(r => setStatements(r.data));
    const loadTransactions = () => {
        if (!selectedStmt) return;
        api.get("/bank/transactions", { params: { statement_id: selectedStmt, status: statusFilter } })
            .then(r => setTransactions(r.data));
    };

    useEffect(() => { loadStatements(); }, []);
    useEffect(() => { loadTransactions(); /* eslint-disable-next-line */ }, [selectedStmt, statusFilter]);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post("/bank/upload", fd);
            toast.success(`Parsed ${r.data.total} transactions · ${r.data.auto_matched} auto-matched`);
            await loadStatements();
            setSelectedStmt(r.data.statement.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); e.target.value = ""; }
    };

    const reconcile = async (txn, cand, amountOverride) => {
        try {
            const body = { match_type: cand.type, match_id: cand.id };
            if (amountOverride != null) body.amount = amountOverride;
            const r = await api.post(`/bank/transactions/${txn.id}/reconcile`, body);
            const st = r.data.payment_status;
            const stLabel = st === "paid" ? "PAID"
                : st === "fully_matched" ? "FULLY MATCHED (awaiting confirm)"
                : st === "partially_paid" ? `PARTIAL (paid ${fmt(r.data.paid_amount)} of ${fmt(r.data.grand_total)})`
                : "linked";
            toast.success(`Reconciled → ${cand.label} · ${stLabel}`);
            loadTransactions();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const unmatch = async (txn) => {
        try {
            await api.post(`/bank/transactions/${txn.id}/unmatch`);
            toast.success("Unmatched");
            loadTransactions();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const saveNote = async (txn, note) => {
        try {
            await api.patch(`/bank/transactions/${txn.id}/note`, { note });
            toast.success("Narration updated");
            loadTransactions();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const markPaid = async (matchType, matchId) => {
        if (!window.confirm("Confirm this target as PAID? This can be reversed later via Mark Unpaid.")) return;
        try {
            const url = matchType === "purchase"
                ? `/purchases/${matchId}/mark-paid`
                : `/invoices/${matchId}/mark-paid`;
            await api.post(url);
            toast.success("Marked as PAID");
            setRefreshKey(k => k + 1);
            loadTransactions();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="bank-recon-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Bank Reconciliation</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Upload bank CSV / PDF → auto-match debits to purchases · credits to invoices ·
                        <span className="font-semibold text-slate-700"> Manual match + partial payments supported</span>
                    </p>
                </div>
                <label className="hdb-btn hdb-btn-primary cursor-pointer" data-testid="bank-upload-btn">
                    {uploading ? <><Loader2 size={13} className="animate-spin"/> Parsing…</>
                               : <><Upload size={13}/> Upload bank CSV / PDF</>}
                    <input type="file" accept=".csv,.pdf,text/csv,application/pdf" className="hidden"
                           onChange={upload} disabled={uploading}
                           data-testid="bank-upload-file" />
                </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left: statement list */}
                <div className="lg:col-span-3">
                    <div className="hdb-card p-3 space-y-1">
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-500 px-2 py-1">
                            Statements ({statements.length})
                        </div>
                        {statements.length === 0 && (
                            <div className="p-4 text-center text-slate-400 text-xs"
                                 data-testid="bank-stmts-empty">
                                No statements yet. Upload a CSV to start.
                            </div>
                        )}
                        {statements.map(s => (
                            <button key={s.id} onClick={() => setSelectedStmt(s.id)}
                                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors
                                                ${selectedStmt === s.id ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}
                                    data-testid={`bank-stmt-${s.id}`}>
                                <div className="font-bold text-sm truncate">{s.label || s.file_name}</div>
                                <div className={`text-[11px] mt-0.5 ${selectedStmt === s.id ? "text-slate-300" : "text-slate-500"}`}>
                                    {s.date_from} → {s.date_to}
                                </div>
                                <div className={`text-[10px] mt-0.5 ${selectedStmt === s.id ? "text-slate-400" : "text-slate-400"}`}>
                                    {s.txn_count} txns · {s.auto_matched} auto-matched
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right: transaction table */}
                <div className="lg:col-span-9">
                    {!selectedStmt ? (
                        <div className="hdb-card p-12 text-center text-slate-400">
                            <FileSpreadsheet size={40} className="mx-auto mb-3 text-slate-300"/>
                            Select a statement on the left, or upload a new CSV.
                        </div>
                    ) : (
                        <>
                            <div className="hdb-card p-3 mb-4 flex flex-wrap gap-1.5 items-center">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-2">Filter:</span>
                                {[
                                    ["", "All"],
                                    ["auto_matched", "Auto-matched"],
                                    ["suggestion", "Suggestions"],
                                    ["unmatched", "No match"],
                                    ["reconciled", "Reconciled"],
                                ].map(([v, label]) => (
                                    <button key={v} onClick={() => setStatusFilter(v)}
                                            className={`text-xs font-bold px-2.5 py-1 rounded-lg
                                                        ${statusFilter === v ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}
                                            data-testid={`bank-filter-${v || "all"}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {transactions.length === 0 ? (
                                <div className="hdb-card p-8 text-center text-slate-400 text-sm"
                                     data-testid="bank-txns-empty">
                                    No transactions match this filter.
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {transactions.map(t => <TxnRow key={t.id} txn={t}
                                        onReconcile={reconcile} onUnmatch={unmatch}
                                        onSaveNote={saveNote}
                                        onManualMatch={setManualForTxn}
                                        onMarkPaid={markPaid}
                                        refreshKey={refreshKey} />)}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {manualForTxn && (
                <ManualMatchModal txn={manualForTxn}
                                  onClose={() => setManualForTxn(null)}
                                  onReconcile={async (cand, amt) => {
                                      await reconcile(manualForTxn, cand, amt);
                                      setManualForTxn(null);
                                  }} />
            )}
        </div>
    );
}

function TxnRow({ txn, onReconcile, onUnmatch, onSaveNote, onManualMatch, onMarkPaid, refreshKey }) {
    const isDebit = txn.debit > 0;
    const amt = isDebit ? txn.debit : txn.credit;
    const [expanded, setExpanded] = useState(txn.status === "suggestion");
    const [noteEdit, setNoteEdit] = useState(false);
    const [noteVal, setNoteVal] = useState(txn.note || "");

    // Confidence color band
    const conf = txn.match_confidence || 0;
    const confCls = conf >= 0.85 ? "bg-emerald-100 text-emerald-800"
                    : conf >= 0.65 ? "bg-amber-100 text-amber-800"
                    : conf > 0     ? "bg-slate-100 text-slate-600"
                    : "";
    const statusBadge = {
        reconciled:   { label: "Reconciled",   cls: "bg-emerald-600 text-white" },
        auto_matched: { label: "Auto-matched", cls: "bg-emerald-100 text-emerald-800" },
        suggestion:   { label: "Suggested",    cls: "bg-amber-100 text-amber-800" },
        unmatched:    { label: "No match",     cls: "bg-slate-200 text-slate-700" },
    }[txn.status] || { label: txn.status, cls: "bg-slate-100" };

    return (
        <div className="hdb-card overflow-hidden" data-testid={`bank-txn-${txn.id}`}>
            <div className="p-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-slate-50/60"
                 onClick={() => setExpanded(!expanded)}>
                <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0
                                ${isDebit ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {isDebit ? <TrendingDown size={14}/> : <TrendingUp size={14}/>}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">
                        {txn.description}
                        {txn.note && (
                            <span className="ml-2 text-[10px] font-bold uppercase text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded"
                                  data-testid={`bank-txn-note-badge-${txn.id}`}>
                                ✎ {txn.note}
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-500">
                        {txn.date}{txn.ref ? ` · ${txn.ref}` : ""}
                    </div>
                </div>
                <div className={`text-right font-mono font-black ${isDebit ? "text-red-700" : "text-emerald-700"}`}>
                    {isDebit ? "−" : "+"} {fmt(amt)}
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadge.cls}`}>
                    {statusBadge.label}
                </span>
                {conf > 0 && (
                    <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${confCls}`}
                          data-testid={`bank-conf-${txn.id}`}>
                        {Math.round(conf * 100)}%
                    </span>
                )}
            </div>

            {expanded && (
                <div className="px-3 pb-3 pt-1 border-t border-slate-100 bg-slate-50/30">
                    {/* Narration edit + Manual match toolbar */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {noteEdit ? (
                            <div className="flex items-center gap-1.5 flex-1 min-w-[240px]">
                                <input type="text" value={noteVal} placeholder="Override narration…"
                                       onChange={e => setNoteVal(e.target.value)}
                                       onClick={e => e.stopPropagation()}
                                       className="flex-1 px-2 py-1 text-xs rounded border border-slate-300 bg-white"
                                       data-testid={`bank-note-input-${txn.id}`}/>
                                <button onClick={(e) => { e.stopPropagation(); onSaveNote(txn, noteVal); setNoteEdit(false); }}
                                        className="hdb-btn hdb-btn-primary text-[10px] px-2 py-1"
                                        data-testid={`bank-note-save-${txn.id}`}>
                                    <Check size={10}/> Save
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setNoteEdit(false); setNoteVal(txn.note || ""); }}
                                        className="text-xs text-slate-500 hover:text-slate-700">
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button onClick={(e) => { e.stopPropagation(); setNoteEdit(true); }}
                                    className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
                                    data-testid={`bank-edit-note-${txn.id}`}>
                                <Pencil size={11}/> {txn.note ? "Edit override narration" : "Add override narration"}
                            </button>
                        )}
                        <div className="ml-auto flex items-center gap-1.5">
                            {txn.status !== "reconciled" && (
                                <button onClick={(e) => { e.stopPropagation(); onManualMatch(txn); }}
                                        className="hdb-btn text-[11px] px-2.5 py-1 bg-indigo-600 text-white hover:bg-indigo-700"
                                        data-testid={`bank-manual-match-${txn.id}`}>
                                    <Search size={11}/> Manual match
                                </button>
                            )}
                        </div>
                    </div>

                    {txn.status === "reconciled" ? (
                        <ReconciledSummary txn={txn}
                                           onUnmatch={onUnmatch}
                                           onManualMatch={onManualMatch}
                                           onMarkPaid={onMarkPaid}
                                           refreshKey={refreshKey} />
                    ) : (txn.candidates?.length > 0) ? (
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                                Possible matches ({txn.candidates.length})
                            </div>
                            <div className="space-y-1.5">
                                {txn.candidates.map((c, i) => (
                                    <div key={i}
                                         className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200"
                                         data-testid={`bank-cand-${txn.id}-${i}`}>
                                        {c.type === "purchase"
                                            ? <Building2 size={11} className="text-slate-500 shrink-0"/>
                                            : <Users size={11} className="text-slate-500 shrink-0"/>}
                                        <div className="flex-1 min-w-0 text-xs">
                                            <div className="font-bold text-slate-900 truncate">{c.label}</div>
                                            <div className="text-[10px] text-slate-500">
                                                {c.type} · {fmt(c.amount)} · {c.date}
                                            </div>
                                        </div>
                                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded
                                                          ${c.confidence >= 0.85 ? "bg-emerald-100 text-emerald-800"
                                                            : c.confidence >= 0.65 ? "bg-amber-100 text-amber-800"
                                                            : "bg-slate-100 text-slate-600"}`}>
                                            {Math.round(c.confidence * 100)}%
                                        </span>
                                        <button onClick={(e) => { e.stopPropagation(); onReconcile(txn, c); }}
                                                className="hdb-btn hdb-btn-primary text-[10px] px-2 py-1"
                                                data-testid={`bank-reconcile-${txn.id}-${i}`}>
                                            <LinkIcon size={10}/> Reconcile
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <AlertTriangle size={11}/>
                            No candidates matched. Click <b>Manual match</b> above to search & link.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function ReconciledSummary({ txn, onUnmatch, onManualMatch, onMarkPaid, refreshKey }) {
    const [target, setTarget] = useState(null);
    useEffect(() => {
        if (!txn.matched_id || !txn.matched_type) return;
        const url = txn.matched_type === "purchase"
            ? `/purchases/${txn.matched_id}`
            : `/invoices/${txn.matched_id}`;
        api.get(url).then(r => setTarget(r.data)).catch(() => setTarget(null));
    }, [txn.matched_id, txn.matched_type, refreshKey]);

    const grand = target?.grand_total || 0;
    const paid = target?.paid_amount || 0;
    const remaining = Math.max(0, grand - paid);
    const status = target?.payment_status || "";

    const statusPill = status === "paid"
        ? { label: "PAID · confirmed", cls: "bg-emerald-600 text-white" }
        : status === "fully_matched"
        ? { label: "Fully matched — awaiting confirm", cls: "bg-blue-100 text-blue-800" }
        : status === "partially_paid"
        ? { label: "Partially paid", cls: "bg-amber-100 text-amber-800" }
        : { label: "Linked", cls: "bg-slate-200 text-slate-700" };

    return (
        <div className="text-xs" data-testid={`bank-reconciled-summary-${txn.id}`}>
            <div className="flex items-center gap-2 flex-wrap p-2 bg-white rounded-lg border border-slate-200">
                <ShieldCheck size={12} className="text-emerald-700 shrink-0"/>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                        {txn.matched_type === "purchase" ? "Purchase" : "Invoice"}: {target?.bill_number || target?.invoice_number || txn.matched_id}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                        <span>Grand {fmt(grand)}</span>
                        <span>· Paid {fmt(paid)}</span>
                        {remaining > 0.5 && <span className="text-amber-700 font-semibold">· Remaining {fmt(remaining)}</span>}
                        {txn.matched_amount && <span>· This txn {fmt(txn.matched_amount)}</span>}
                    </div>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusPill.cls}`}
                      data-testid={`bank-target-status-${txn.id}`}>
                    {statusPill.label}
                </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {status !== "paid" && (
                    <button onClick={(e) => { e.stopPropagation(); onMarkPaid(txn.matched_type, txn.matched_id); }}
                            className="hdb-btn text-[11px] px-2.5 py-1 bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`bank-mark-paid-${txn.id}`}>
                        <CheckCircle2 size={11}/> Mark as PAID
                    </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); onManualMatch(txn); }}
                        className="hdb-btn text-[11px] px-2.5 py-1 bg-slate-100 hover:bg-slate-200"
                        data-testid={`bank-change-match-${txn.id}`}>
                    <Search size={11}/> Add another match / change
                </button>
                <button onClick={(e) => { e.stopPropagation(); onUnmatch(txn); }}
                        className="hdb-btn text-[11px] px-2.5 py-1 text-red-600 hover:bg-red-50 ml-auto"
                        data-testid={`bank-unmatch-${txn.id}`}>
                    <X size={11}/> Unmatch
                </button>
            </div>
        </div>
    );
}


function ManualMatchModal({ txn, onClose, onReconcile }) {
    const isDebit = txn.debit > 0;
    const txnAmt = isDebit ? txn.debit : txn.credit;
    const defaultTab = isDebit ? "purchases" : "invoices";
    const [tab, setTab] = useState(defaultTab);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState({ purchases: [], invoices: [] });
    const [selected, setSelected] = useState(null);
    const [amount, setAmount] = useState(txnAmt);
    const debounceRef = useRef();

    useEffect(() => {
        if (!q || q.length < 2) { setResults({ purchases: [], invoices: [] }); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.get("/bank/search-targets", { params: { q, limit: 30 } });
                setResults(r.data);
            } catch (e) { /* noop */ }
            finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [q]);

    const list = tab === "purchases" ? results.purchases : results.invoices;

    const submit = () => {
        if (!selected) { toast.error("Pick an invoice or purchase first"); return; }
        const amt = parseFloat(amount);
        if (isNaN(amt) || amt <= 0) { toast.error("Amount must be positive"); return; }
        if (amt > txnAmt + 0.5) {
            toast.error(`Payment amount (${fmt(amt)}) can't exceed transaction amount (${fmt(txnAmt)})`);
            return;
        }
        onReconcile(selected, amt);
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
             onClick={onClose}
             data-testid="bank-manual-match-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0
                                     ${isDebit ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {isDebit ? <TrendingDown size={18}/> : <TrendingUp size={18}/>}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-black hdb-heading">Manual Match</h2>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                            {txn.date} · {txn.description} · {" "}
                            <span className="font-mono font-bold text-slate-900">
                                {isDebit ? "−" : "+"} {fmt(txnAmt)}
                            </span>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"
                            data-testid="bank-manual-close">
                        <X size={18}/>
                    </button>
                </div>

                {/* Tabs */}
                <div className="px-4 pt-3 flex gap-1.5 border-b border-slate-200">
                    {["purchases", "invoices"].map(t => (
                        <button key={t} onClick={() => setTab(t)}
                                className={`text-xs font-bold px-3 py-1.5 rounded-t-lg
                                            ${tab === t ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                data-testid={`bank-manual-tab-${t}`}>
                            {t === "purchases" ? <><Building2 size={11} className="inline mr-1"/> Purchases</>
                                               : <><Users size={11} className="inline mr-1"/> Invoices</>}
                            {" "}({(t === "purchases" ? results.purchases : results.invoices).length})
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="p-4 border-b border-slate-200">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                        <input type="text" placeholder="Search by vendor/client name, number, GSTIN, or amount…"
                               value={q} onChange={e => setQ(e.target.value)} autoFocus
                               className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-slate-900"
                               data-testid="bank-manual-search"/>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1.5">
                        {loading ? "Searching…" : q.length < 2 ? "Type at least 2 characters"
                            : `${list.length} ${tab} found`}
                    </div>
                </div>

                {/* Results */}
                <div className="flex-1 overflow-y-auto p-3">
                    {list.length === 0 && q.length >= 2 && !loading && (
                        <div className="text-center text-slate-400 text-sm py-8">
                            No {tab} found. Try a different search term.
                        </div>
                    )}
                    {list.map(item => {
                        const isSelected = selected?.id === item.id && selected?.type === item.type;
                        const remaining = item.remaining || 0;
                        const paid = item.paid_amount || 0;
                        return (
                            <button key={item.id} onClick={() => setSelected(item)}
                                    className={`w-full text-left p-3 rounded-lg border mb-1.5 transition-colors
                                                ${isSelected ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200"
                                                            : "border-slate-200 bg-white hover:bg-slate-50"}`}
                                    data-testid={`bank-manual-result-${item.type}-${item.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm text-slate-900 truncate">{item.label}</div>
                                        <div className="text-[11px] text-slate-500">
                                            {item.date} · Grand {fmt(item.amount)}
                                            {paid > 0 && <> · Paid {fmt(paid)}</>}
                                            {remaining > 0.5 && paid > 0 && <> · <span className="text-amber-700 font-semibold">Remaining {fmt(remaining)}</span></>}
                                        </div>
                                    </div>
                                    {item.payment_status && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase
                                                          ${item.payment_status === "paid" ? "bg-emerald-600 text-white"
                                                            : item.payment_status === "fully_matched" ? "bg-blue-100 text-blue-800"
                                                            : "bg-amber-100 text-amber-800"}`}>
                                            {item.payment_status.replace("_", " ")}
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Footer / Amount */}
                <div className="p-4 border-t border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="flex-1 min-w-[200px]">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                                <Wallet size={11} className="inline mr-1"/> Payment amount for this txn
                            </label>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-500 font-mono">₹</span>
                                <input type="number" step="0.01" value={amount}
                                       onChange={e => setAmount(e.target.value)}
                                       className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono focus:outline-none focus:border-slate-900"
                                       data-testid="bank-manual-amount"/>
                                <button type="button" onClick={() => setAmount(txnAmt)}
                                        className="text-[10px] text-slate-500 hover:text-slate-800 underline">
                                    reset
                                </button>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">
                                Txn total {fmt(txnAmt)}
                                {selected && <> · Selected {selected.type} remaining {fmt(selected.remaining || 0)}</>}
                                {selected && parseFloat(amount) < (selected.amount || 0) - 0.5 && (
                                    <span className="text-amber-700 ml-1 font-semibold">· Partial payment</span>
                                )}
                            </div>
                        </div>
                        <button onClick={submit}
                                disabled={!selected}
                                className={`hdb-btn hdb-btn-primary ${!selected ? "opacity-40 cursor-not-allowed" : ""}`}
                                data-testid="bank-manual-submit">
                            <LinkIcon size={13}/> Save Match
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
